/**
 * Frontend report hub URLs under /properties (not /pms-suppliers).
 * Backend APIs remain on /pms-suppliers/:supplierId/hotels/... and /pms-csv-hotels/...
 */

/**
 * @param {string|number} supplierId
 * @param {string|number} hotelCode
 */
export function pmsHotelReportsBase(supplierId, hotelCode) {
  if (supplierId == null || hotelCode == null || String(hotelCode).trim() === '') return null;
  return `/properties/pms/${supplierId}/${encodeURIComponent(String(hotelCode))}`;
}

/**
 * @param {string|number} csvHotelId
 */
export function csvHotelReportsBase(csvHotelId) {
  if (csvHotelId == null || String(csvHotelId).trim() === '') return null;
  return `/properties/csv/${csvHotelId}`;
}

/**
 * @param {string|number} propertyId
 */
export function manualPropertyReportsBase(propertyId) {
  if (propertyId == null || String(propertyId).trim() === '') return null;
  return `/properties/${propertyId}`;
}

/**
 * Property CRM profile path for a unified Properties directory row.
 * @param {{ isCsvHotel?: boolean, csvHotelId?: string|number, isPmsHotel?: boolean, isPushPmsHotel?: boolean, pmsHotelData?: { id?: string|number }, id?: string|number }} property
 * @returns {string|null}
 */
export function propertyCrmProfileHref(property) {
  if (!property) return null;
  if (property.isCsvHotel && property.csvHotelId != null) {
    return `/properties/crm/pms_csv_hotel/${property.csvHotelId}`;
  }
  if (property.isPmsHotel && property.pmsHotelData?.id != null) {
    const registry = property.isPushPmsHotel ? 'pms_push_hotel' : 'pms_hotel';
    return `/properties/crm/${registry}/${property.pmsHotelData.id}`;
  }
  if (property.id != null && !String(property.id).startsWith('pms') && !String(property.id).startsWith('csv')) {
    return `/properties/crm/property/${property.id}`;
  }
  return null;
}

/**
 * Reports hub path for a unified Properties directory row.
 * @param {{ isCsvHotel?: boolean, csvHotelId?: string|number, isPmsHotel?: boolean, supplierId?: string|number, hotelCode?: string|number, pmsHotelData?: { id?: string|number }, id?: string|number }} property
 */
export function propertyReportsHref(property) {
  if (!property) return '/properties';
  if (property.isCsvHotel) {
    const base = csvHotelReportsBase(property.csvHotelId);
    return base ? `${base}/reports` : '/properties';
  }
  if (property.isPmsHotel) {
    const hotelKey = property.hotelCode ?? property.pmsHotelData?.id;
    const base = pmsHotelReportsBase(property.supplierId, hotelKey);
    return base ? `${base}/reports` : '/admin/pms-suppliers#push-pms';
  }
  const base = manualPropertyReportsBase(property.id);
  return base ? `${base}/reports` : '/properties';
}

/**
 * Redirect target from legacy UI paths to the new /properties/... hubs.
 * @param {{ supplierId?: string, hotelCode?: string, csvHotelId?: string, rest?: string }} parts
 * @param {'pms'|'csv'} kind
 */
export function legacyHotelReportsRedirectPath(kind, parts) {
  if (kind === 'csv') {
    const base = csvHotelReportsBase(parts.csvHotelId);
    if (!base) return '/properties';
    const rest = parts.rest ? `/${String(parts.rest).replace(/^\//, '')}` : '/reports';
    return `${base}${rest}`;
  }
  const base = pmsHotelReportsBase(parts.supplierId, parts.hotelCode);
  if (!base) return '/properties';
  const rest = parts.rest ? `/${String(parts.rest).replace(/^\//, '')}` : '/reports';
  return `${base}${rest}`;
}
